import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MenuItem, SelectItem } from 'primeng/api';
import { DatagridService, NotificationService, PagedResultGeneric, TranslationService } from 'src/app/shared';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { ProfileRelationSearch } from '../../shared/profile-relation-search.model';
import { ProfileService } from '../../shared/profile.service';
import { RelationTypeSearchParams } from '../../shared/relation-type-search-params.model';
import { RelationTypeService } from '../../shared/relation-type.service';
import { ProfileRelationshipManagementDto } from '../../shared/dto/profile-relationship-management.dto';
import { initializedProfilePermissionDto } from '../../shared/dto/profile-permission.dto';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounce } from 'lodash-decorators';
import { ObservationService } from 'src/app/shared/observation/observation.service';
import { UserService } from 'src/app/users/shared/user.service';
import { SecurityAccessLevelEnum } from 'src/app/shared/auth/enum/security-access-level.enum';

@Component({
  selector: 'wl-profile-relationship-grid',
  templateUrl: './profile-relationship-grid.component.html',
  styleUrls: ['./profile-relationship-grid.component.less'],
    standalone: false
})
export class ProfileRelationshipGridComponent
  extends LazySelectableTableComponent<ProfileRelationSearch, ProfileRelationshipManagementDto>
  implements OnInit, OnDestroy
{
  @Output() editClicked: EventEmitter<ProfileRelationshipManagementDto> = new EventEmitter();
  @Output() deleteClicked: EventEmitter<number> = new EventEmitter();
  booleanOptions: SelectItem[] = [];
  relationTypeOptions: SelectItem[] = [];
  rowMenuItems = new Map<number, MenuItem[]>();
  relatedProfileAccess: number = 0;
  profileContactsTabFullAccess: boolean = false;
  private _subs = new Subscription();
  constructor(
    datagridService: DatagridService,
    private _profileService: ProfileService,
    private _translationService: TranslationService,
    private _relationTypeService: RelationTypeService,
    private _notificationService: NotificationService,
    private _observationService: ObservationService,
    private _userService: UserService
  ) {
    super('RelationID', datagridService);
    this.searchParams = new ProfileRelationSearch();
  }

  fetchResults(): Promise<PagedResultGeneric<ProfileRelationshipManagementDto>> {
    return this._profileService.getProfileRelationsForManagement(this.searchParams, true);
  }

  ngOnInit(): void {
    this._userService.getCurrentUserSecurityElementAccessLevel('ProfileRelated_Profiles_Tab').then((accessLevel: number) => {
      this.relatedProfileAccess = accessLevel;
    });

    this._userService.getCurrentUserSecurityElementAccessLevel('ProfileContacts_Tab').then((userAccessLevel: number) => {
      this.profileContactsTabFullAccess = userAccessLevel > SecurityAccessLevelEnum.ReadOnlyAccess;
    });

    this.initFilterOptions();
    this._subs.add(
      this._observationService.observeProfileRelations().subscribe(() => {
        this.search();
      })
    );
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
  }

  actionItemsForRelation(relation: ProfileRelationshipManagementDto): MenuItem[] {
    if (!this.rowMenuItems.has(relation.RelationID)) {
      this.rowMenuItems.set(relation.RelationID, [
        {
          label: this._translationService.getInstantTitlecase('common.edit'),
          command: () => {
            this.editClicked.emit(relation);
          },
        },
        {
          label: this._translationService.getInstantTitlecase('common.delete'),
          visible: this.profileContactsTabFullAccess,
          command: () => {
            this.deleteClicked.emit(relation.RelationID);
          },
        },
      ]);
    }
    return this.rowMenuItems.get(relation.RelationID);
  }

  pageSizeChanged() {
    this.searchParams.PageNumber = 1;
    this.search();
  }

  @debounce(500)
  filterChanged() {
    this.search();
  }

  isEditorToggled(profileRelationship: ProfileRelationshipManagementDto) {
    let editPromise: Promise<any>;
    if (profileRelationship.IsEditor) {
      const newPermission = initializedProfilePermissionDto({
        ProfileId: profileRelationship.RelProfileID,
        TargetProfileId: profileRelationship.ProfileID,
      });
      editPromise = this._profileService.createProfilePermission(newPermission).then((data) => {
        newPermission.Id = data;
        profileRelationship.EditProfilePermission = newPermission;
        profileRelationship.EditProfilePermissionId = newPermission.Id;
      });
    } else {
      editPromise = this._profileService.deleteProfilePermission(profileRelationship.EditProfilePermission);
    }

    editPromise.then(() => {
      this._notificationService.translateSuccess('profiles.relationships.edit-permission-saved-messsage');
    });
  }

  mainContactToggled(profileRelationship: ProfileRelationshipManagementDto) {
    this.updateRelationship(profileRelationship, 'profiles.relationships.main-contact-saved-messsage').then(() => {
      if (!profileRelationship.MainContact) {
        return;
      }
      this.searchResult?.Result?.forEach?.((relation) => {
        if (relation.MainContact && relation.RelationID !== profileRelationship.RelationID) {
          relation.MainContact = false;
        }
      });
    });
  }

  billingContactToggled(profileRelationship: ProfileRelationshipManagementDto) {
    this.updateRelationship(profileRelationship, 'profiles.relationships.billing-saved-messsage').then(() => {
      if (!profileRelationship.BillingContact) {
        return;
      }
      this.searchResult?.Result?.forEach?.((relation) => {
        if (relation.BillingContact && relation.RelationID !== profileRelationship.RelationID) {
          relation.BillingContact = false;
        }
      });
    });
  }

  receivesCommunicationToggled(profileRelationship: ProfileRelationshipManagementDto) {
    this.updateRelationship(profileRelationship, 'profiles.relationships.receives-email-saved-messsage');
  }

  private updateRelationship(profileRelation: ProfileRelationshipManagementDto, successTranslateKey: string): Promise<void> {
    return this._profileService
      .updateProfileRelation(profileRelation)
      .then(() => {
        this._notificationService.translateSuccess(successTranslateKey);
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      });
  }

  private async initFilterOptions() {
    this.booleanOptions = [
      {
        label: await this._translationService.getCapitalizeFirstCharacter('common.true'),
        value: true,
      },
      {
        label: await this._translationService.getCapitalizeFirstCharacter('common.false'),
        value: false,
      },
    ];

    const relationTypeParams = new RelationTypeSearchParams();
    relationTypeParams.PageSize = 0;
    this._relationTypeService.search(relationTypeParams).then((result) => {
      this.relationTypeOptions = result.Result?.map?.((type) => {
        return { label: type.RelationType, value: type.RelationType };
      });
    });
  }
}
